import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import "../../src/assets/css/style.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "react-toastify/dist/ReactToastify.css";

import footer from "../../src/assets/img/mainfooterbg.png";

import { ToastContainer, toast } from "react-toastify";
import {
  getCertificateListForCitizen,
  getSummaryReport,
} from "../CertificateService";

import Navbar from "./Navbar";

const AchievementList = () => {
  const navigate = useNavigate();

  const [certificateDataList, setCertificateDataList] = useState([]);
  const [globalData, setGlobalData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const getFormattedDate = () => {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const safeNumber = (value) => {
    const parsedValue = Number.parseInt(value, 10);

    return Number.isNaN(parsedValue) ? 0 : parsedValue;
  };

  const getCertificateTypes = (certificate) => {
    const certificateTypes = [
      certificate?.CHAR_CERT,
      certificate?.INCM_CERT,
      certificate?.RESI_CERT,
      certificate?.SPER_CERT,
      certificate?.DIST_CERT,
      certificate?.CAST_CERT,
      certificate?.UNMARRIED_CERT,
    ];

    return certificateTypes.filter(
      (certificateType) =>
        certificateType &&
        certificateType !== "-" &&
        certificateType.trim() !== ""
    );
  };

  const getStatusClass = (status = "") => {
    const normalizedStatus = status.toLowerCase().trim();

    if (
      normalizedStatus.includes("ready") ||
      normalizedStatus.includes("approved")
    ) {
      return "certificate-status-ready";
    }

    if (
      normalizedStatus.includes("progress") ||
      normalizedStatus.includes("pending")
    ) {
      return "certificate-status-progress";
    }

    if (normalizedStatus.includes("reject")) {
      return "certificate-status-rejected";
    }

    return "certificate-status-default";
  };

  useEffect(() => {
    let isMounted = true;

    const fetchCertificateData = async () => {
      setIsLoading(true);

      let storedUserData = null;

      try {
        const certificateUser = localStorage.getItem("Certificate_user");

        if (certificateUser) {
          storedUserData = JSON.parse(certificateUser);
        }
      } catch (error) {
        console.error("Unable to read citizen data:", error);

        toast.error("Unable to read citizen login information.");
      }

      if (!storedUserData?.mobile) {
        if (isMounted) {
          setCertificateDataList([]);
          setGlobalData([]);
          setIsLoading(false);
        }

        toast.error(
          "Citizen mobile number was not found. Please log in again."
        );

        return;
      }

      try {
        const certificateRequest = getCertificateListForCitizen(
          storedUserData.mobile
        );

        const summaryRequest = getSummaryReport(
          0,
          0,
          0,
          "01.01.2024",
          getFormattedDate()
        );

        const [certificateResult, summaryResult] =
          await Promise.allSettled([
            certificateRequest,
            summaryRequest,
          ]);

        if (!isMounted) {
          return;
        }

        if (certificateResult.status === "fulfilled") {
          const certificateResponse =
            certificateResult.value?.data;

          setCertificateDataList(
            Array.isArray(certificateResponse)
              ? certificateResponse
              : []
          );
        } else {
          console.error(
            "Certificate list error:",
            certificateResult.reason
          );

          setCertificateDataList([]);

          toast.error(
            "Unable to load certificate applications."
          );
        }

        if (summaryResult.status === "fulfilled") {
          const summaryResponse = summaryResult.value?.data;

          setGlobalData(
            Array.isArray(summaryResponse)
              ? summaryResponse
              : []
          );
        } else {
          console.error(
            "Summary report error:",
            summaryResult.reason
          );

          setGlobalData([]);

          toast.error(
            "Unable to load application summary."
          );
        }
      } catch (error) {
        console.error("Unexpected certificate page error:", error);

        if (isMounted) {
          setCertificateDataList([]);
          setGlobalData([]);
        }

        toast.error(
          "Something went wrong while loading the page."
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCertificateData();

    return () => {
      isMounted = false;
    };
  }, []);

  const summaryTotals = useMemo(() => {
    return globalData.reduce(
      (totals, item) => {
        totals.totalApply += safeNumber(item?.apply);
        totals.totalPending += safeNumber(item?.inProgress);
        totals.totalDownload += safeNumber(item?.download);
        totals.totalReject += safeNumber(item?.reject);

        return totals;
      },
      {
        totalApply: 0,
        totalPending: 0,
        totalDownload: 0,
        totalReject: 0,
      }
    );
  }, [globalData]);

  const summaryCards = [
    {
      title: "Total Applications",
      value: summaryTotals.totalApply,
      icon: "fa-file-lines",
      className: "summary-total",
    },
    {
      title: "In Progress",
      value: summaryTotals.totalPending,
      icon: "fa-clock",
      className: "summary-progress",
    },
    {
      title: "Ready to Download",
      value: summaryTotals.totalDownload,
      icon: "fa-circle-check",
      className: "summary-download",
    },
    {
      title: "Rejected",
      value: summaryTotals.totalReject,
      icon: "fa-circle-xmark",
      className: "summary-rejected",
    },
  ];

  const handleApplyCertificate = () => {
    navigate("/add-certificate");
  };

  return (
    <div className="certificate-page">
      <ToastContainer
        position="top-right"
        autoClose={3500}
        newestOnTop
        closeOnClick
        pauseOnHover
      />

      <Navbar />

      <main className="certificate-main">
        <section className="certificate-page-header">
          <div className="certificate-heading-content">
            <div className="certificate-heading-icon">
              <i className="fa-solid fa-award" />
            </div>

            <div>
              <p className="certificate-heading-label">
                Citizen Corner
              </p>

              <h1>Certificate Applications</h1>

              <p className="certificate-heading-description">
                Track your applications, check their current
                status and download approved certificates.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="certificate-apply-button"
            onClick={handleApplyCertificate}
          >
            <i className="fa-solid fa-plus" />

            <span>Apply for Certificate</span>
          </button>
        </section>

        <section className="certificate-summary-grid">
          {summaryCards.map((card) => (
            <article
              key={card.title}
              className={`certificate-summary-card ${card.className}`}
            >
              <div className="certificate-summary-icon">
                <i
                  className={`fa-solid ${card.icon}`}
                />
              </div>

              <div className="certificate-summary-content">
                <span>{card.title}</span>

                <strong>
                  {card.value.toLocaleString("en-IN")}
                </strong>
              </div>

              <div className="certificate-summary-decoration" />
            </article>
          ))}
        </section>


        <section className="certificate-table-card">
          <div className="certificate-table-header">
            <div>
              <p className="certificate-table-label">
                My applications
              </p>

              <h2>Certificate Application List</h2>
            </div>

            <div className="certificate-record-count">
              <i className="fa-solid fa-list-check" />

              <span>
                {certificateDataList.length}{" "}
                {certificateDataList.length === 1
                  ? "Record"
                  : "Records"}
              </span>
            </div>
          </div>

          <div className="certificate-table-responsive">
            <table className="certificate-table">
              <thead>
                <tr>
                  <th>Certificate Type</th>
                  <th>Applicant</th>
                  <th>Application Date</th>
                  <th>Valid Until</th>
                  <th>Status</th>

                  <th className="certificate-action-heading">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan="6">
                      <div className="certificate-loading-state">
                        <div className="certificate-loader" />

                        <h3>Loading applications</h3>

                        <p>
                          Please wait while your certificate
                          applications are being loaded.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  certificateDataList.length === 0 && (
                    <tr>
                      <td colSpan="6">
                        <div className="certificate-empty-state">
                          <div className="certificate-empty-icon">
                            <i className="fa-regular fa-folder-open" />
                          </div>

                          <h3>
                            No certificate applications found
                          </h3>

                          <p>
                            You have not submitted any
                            certificate application yet.
                          </p>

                          <button
                            type="button"
                            className="certificate-empty-button"
                            onClick={handleApplyCertificate}
                          >
                            <i className="fa-solid fa-plus" />

                            <span>Apply Now</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                {!isLoading &&
                  certificateDataList.map(
                    (certificate, index) => {
                      const certificateTypes =
                        getCertificateTypes(certificate);

                      const isReadyToDownload =
                        certificate?.STATUS ===
                        "Ready To Download";

                      return (
                        <tr
                          key={
                            certificate?.APPLICATION_ID ||
                            `certificate-${index}`
                          }
                        >
                          <td data-label="Certificate Type">
                            {certificateTypes.length > 0 ? (
                              <ul className="certificate-type-list">
                                {certificateTypes.map(
                                  (
                                    certificateType,
                                    typeIndex
                                  ) => (
                                    <li
                                      key={`${certificateType}-${typeIndex}`}
                                    >
                                      <i className="fa-solid fa-certificate" />

                                      <span>
                                        {certificateType}
                                      </span>
                                    </li>
                                  )
                                )}
                              </ul>
                            ) : (
                              <span className="certificate-not-available">
                                —
                              </span>
                            )}
                          </td>

                          <td data-label="Applicant">
                            <div className="certificate-applicant">
                              <div className="certificate-applicant-avatar">
                                {certificate?.NAME_APPLI
                                  ? certificate.NAME_APPLI
                                      .charAt(0)
                                      .toUpperCase()
                                  : "A"}
                              </div>

                              <div>
                                <strong>
                                  {certificate?.NAME_APPLI ||
                                    "Not available"}
                                </strong>

                                {certificate?.APPLICATION_ID && (
                                  <span>
                                    ID:{" "}
                                    {
                                      certificate.APPLICATION_ID
                                    }
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          <td data-label="Application Date">
                            <div className="certificate-date">
                              <i className="fa-regular fa-calendar" />

                              <span>
                                {certificate?.SAVE_DT || "—"}
                              </span>
                            </div>
                          </td>

                          <td data-label="Valid Until">
                            <div className="certificate-date">
                              <i className="fa-regular fa-calendar-check" />

                              <span>
                                {certificate?.VALID_DT || "—"}
                              </span>
                            </div>
                          </td>

                          <td data-label="Status">
                            <div className="certificate-status-wrapper">
                              <span
                                className={`certificate-status ${getStatusClass(
                                  certificate?.STATUS
                                )}`}
                              >
                                <span className="certificate-status-dot" />

                                {certificate?.STATUS ||
                                  "Status unavailable"}
                              </span>

                              {certificate?.STATUS ===
                                "Rejected" &&
                                certificate?.PRADHAN_RMKS && (
                                  <p className="certificate-rejection-remark">
                                    {
                                      certificate.PRADHAN_RMKS
                                    }
                                  </p>
                                )}
                            </div>
                          </td>

                          <td
                            data-label="Action"
                            className="certificate-action-cell"
                          >
                            <div className="certificate-action-buttons">
                              <Link
                                to="/application-details"
                                state={{
                                  applicationId:
                                    certificate?.APPLICATION_ID,
                                }}
                                className="certificate-action-button certificate-view-button"
                                title="View application details"
                              >
                                <i className="fa-solid fa-eye" />

                                <span>View</span>
                              </Link>

                              {isReadyToDownload && (
                                <div className="certificate-download-actions">
                                  <Link
                                    to="/Ecertificate-download"
                                    state={{
                                      applicationId:
                                        certificate?.APPLICATION_ID,
                                    }}
                                    className="certificate-action-button certificate-download-button"
                                    title="Download English certificate"
                                  >
                                    <i className="fa-solid fa-download" />

                                    <span>English</span>
                                  </Link>

                                  {certificate?.OTH_LANG ===
                                    "B" && (
                                    <Link
                                      to="/Bcertificate-download"
                                      state={{
                                        applicationId:
                                          certificate?.APPLICATION_ID,
                                      }}
                                      className="certificate-action-button certificate-language-button"
                                      title="Download Bengali certificate"
                                    >
                                      <i className="fa-solid fa-language" />

                                      <span>Bengali</span>
                                    </Link>
                                  )}

                                  {certificate?.OTH_LANG ===
                                    "N" && (
                                    <Link
                                      to="/Bcertificate-download"
                                      state={{
                                        applicationId:
                                          certificate?.APPLICATION_ID,
                                      }}
                                      className="certificate-action-button certificate-language-button"
                                      title="Download Nepali certificate"
                                    >
                                      <i className="fa-solid fa-language" />

                                      <span>Nepali</span>
                                    </Link>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <footer className="certificate-footer">
        <img
          src={footer}
          className="certificate-footer-image"
          alt=""
          aria-hidden="true"
        />

        <div className="certificate-footer-content">
          <p>
            <strong>© 2024</strong> Designed and Developed by{" "}
            <b>IT and Statistical Cell</b>, Panchayat &amp;
            Rural Development Department,{" "}
            <b>Government of West Bengal</b>
          </p>

          <p>
            Site best viewed at 1920 × 1080 resolution using
            the latest version of Google Chrome, Mozilla
            Firefox, Microsoft Edge or Safari.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AchievementList;